import React from 'react';
import { motion } from 'framer-motion';
import { Terminal, Brain, Lightbulb, Code2 } from 'lucide-react';

const FounderSection = () => {
  return (
    <section className="py-20 lg:py-24 bg-slate-50 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-br from-orcsys-500/5 to-transparent rounded-bl-full" />
      <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-orcsys-500/5 to-transparent rounded-tr-full" />

      <div className="container px-4 mx-auto relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="relative order-2 lg:order-1"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1571171637578-41bc2dd41cd2?q=80&w=2070&auto=format&fit=crop"
                  alt="Fondateur au travail"
                  className="w-full h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-transparent" />
                
                {/* Floating badges */}
                <div className="absolute inset-0 p-6">
                  <div className="h-full flex flex-col justify-end text-white">
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { icon: <Terminal className="w-5 h-5" />, text: "Full-Stack Engineer" },
                        { icon: <Brain className="w-5 h-5" />, text: "Problem Solver" },
                        { icon: <Lightbulb className="w-5 h-5" />, text: "Innovateur" },
                        { icon: <Code2 className="w-5 h-5" />, text: "Tech Enthusiast" }
                      ].map((badge, index) => (
                        <motion.div
                          key={index}
                          className="bg-white/10 backdrop-blur-sm rounded-lg p-3 flex items-center space-x-2"
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          viewport={{ once: true }}
                        >
                          {badge.icon}
                          <span className="text-white text-sm font-medium">{badge.text}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Code snippet decoration */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-slate-900 rounded-lg shadow-xl overflow-hidden hidden lg:block">
                <div className="p-4 text-[10px] font-mono text-slate-300/90">
                  <span className="text-orcsys-400">const</span>{" "}
                  <span className="text-purple-400">passion</span>{" "}
                  <span className="text-slate-300/90">=</span>{" "}
                  <span className="text-green-400">'coding'</span>;
                </div>
              </div>
            </motion.div>

            <motion.div
              className="order-1 lg:order-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Un ingénieur attiré par <span className="text-orcsys-500">l'innovation</span>
              </h2>

              <div className="space-y-6 text-lg text-slate-600">
                <p>
                  En tant que fondateur et leader technique d'Orcsys depuis 2023, je combine ma passion pour 
                  le code avec une vision claire : créer des solutions qui apportent une réelle valeur
                  aux entrepreneurs.
                </p>

                <p>
                  Ma philosophie est simple : fournir un travail d'excellence, maintenir une 
                  communication transparente, et établir des partenariats durables avec nos clients. 
                  Je crois fermement que le succès de nos clients est notre succès.
                </p>

                <div className="pt-4">
                  <div className="inline-flex items-center px-4 py-2 rounded-full bg-orcsys-50 text-orcsys-600">
                    <Code2 className="w-5 h-5 mr-2" />
                    <span className="font-medium">Passionné par le code depuis 2014</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FounderSection; 